import {AfterViewInit, Component, OnInit} from '@angular/core';
import {ModalInterface} from "../../modal.interface";
import {Observable, Subject} from "rxjs";
import {ModalStateEvent} from "../../modal-state-event";
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {ModalState} from "../../modal-state.enum";
import {RxwebValidators} from "@rxweb/reactive-form-validators";
import {CostDataProviderService} from "../../../../features/user/project/goals/cost.data-provider.service";
import {ProjectViewModel} from "../../../../features/user/garage/project.view-model";
import {GoalViewModel} from "../../../../features/user/project/goals/goal.view-model";
import {CostViewModel} from "../../../../features/user/project/goals/cost.view-model";
import {ConfirmationService} from "primeng/api";
import {TranslateService} from "@ngx-translate/core";
import {NumberHelper} from "../../../../core/helpers/number.helper";

type CostLinkType = 'none' | 'goal' | 'task';

@Component({
  selector: 'app-add-cost',
  templateUrl: './add-cost.component.html',
  styleUrls: ['./add-cost.component.scss'],
  providers: [CostDataProviderService]
})
export class AddCostComponent implements ModalInterface, OnInit, AfterViewInit {
  public data: { projectVM: ProjectViewModel; goalVMs: GoalViewModel[]; costVM?: CostViewModel };
  public state: Observable<ModalStateEvent>;
  public subject: Subject<ModalStateEvent>;
  public form: UntypedFormGroup;
  public costVM: CostViewModel | null = null;
  public goalOptions: Array<{label: string; value: string}> = [];
  public taskOptions: Array<{label: string; value: string}> = [];
  public linkTypeOptions: Array<{label: string; value: CostLinkType}> = [];

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly costDPS: CostDataProviderService,
    private readonly confirmationService: ConfirmationService,
    private readonly translate: TranslateService,
  ) {
  }

  ngOnInit(): void {
    this.costVM = this.data.costVM || null;
    this.goalOptions = (this.data.goalVMs || []).map((goal: GoalViewModel) => ({
      label: goal.name,
      value: goal['@id'],
    }));
    this.taskOptions = (this.data.goalVMs || []).flatMap((goal: GoalViewModel) =>
      (goal.tasks || []).map((task) => ({
        label: `${goal.name} / ${task.name}`,
        value: task['@id'],
      }))
    );
    this.linkTypeOptions = [
      {label: this.translate.instant('shared.modal.component.add_cost.link_type.none'), value: 'none'},
      {label: this.translate.instant('shared.modal.component.add_cost.link_type.goal'), value: 'goal'},
      {label: this.translate.instant('shared.modal.component.add_cost.link_type.task'), value: 'task'},
    ];
    this.createForm();
    this.watchFormStatusChange();
  }

  ngAfterViewInit(): void {
  }

  get isGoalLink(): boolean {
    return this.form?.get('linkType')?.value === 'goal';
  }

  get isTaskLink(): boolean {
    return this.form?.get('linkType')?.value === 'task';
  }

  private createForm(): void {
    const initialLinkType = this.resolveInitialLinkType();

    this.form = this.fb.group({
      name: new UntypedFormControl(this.costVM?.name || null, [
        RxwebValidators.required(),
        RxwebValidators.minLength({value: 3}),
        RxwebValidators.maxLength({value: 255})
      ]),
      description: new UntypedFormControl(this.costVM?.description || null, [
        RxwebValidators.maxLength({value: 10000})
      ]),
      priceNet: new UntypedFormControl(this.costVM ? NumberHelper.toNumber(this.costVM.priceNet) : null, [
        RxwebValidators.required(),
        Validators.min(0.01)
      ]),
      incurredAt: new UntypedFormControl(this.costVM?.incurredAt || new Date(), [
        RxwebValidators.required(),
        RxwebValidators.date()
      ]),
      linkType: new UntypedFormControl(initialLinkType),
      goal: new UntypedFormControl(initialLinkType === 'goal' ? this.costVM?.goal || null : null),
      task: new UntypedFormControl(initialLinkType === 'task' ? this.costVM?.task || null : null),
    });

    this.applyLinkTypeValidators(initialLinkType);

    if (this.form.valid) {
      this.subject.next(new ModalStateEvent(ModalState.VALID));
    }

    this.form.get('linkType').valueChanges.subscribe((value: CostLinkType) => {
      if (value !== 'goal') {
        this.form.get('goal').setValue(null, {emitEvent: false});
      }
      if (value !== 'task') {
        this.form.get('task').setValue(null, {emitEvent: false});
      }
      this.applyLinkTypeValidators(value);
      this.emitFormState();
    });

    this.form.valueChanges.subscribe(() => this.emitFormState());
  }

  private watchFormStatusChange(): void {
    this.state.subscribe((mse: ModalStateEvent) => {
      if (mse.status === ModalState.PENDING) {
        this.submit();
      }
    });
  }

  private emitFormState(): void {
    if (this.form.valid) {
      this.subject.next(new ModalStateEvent(ModalState.VALID));
    } else {
      this.subject.next(new ModalStateEvent(ModalState.INVALID));
    }
  }

  private resolveInitialLinkType(): CostLinkType {
    if (this.costVM?.task) {
      return 'task';
    }
    if (this.costVM?.goal) {
      return 'goal';
    }

    return 'none';
  }

  private applyLinkTypeValidators(value: CostLinkType): void {
    const goalControl = this.form.get('goal');
    const taskControl = this.form.get('task');

    goalControl.clearValidators();
    taskControl.clearValidators();

    if (value === 'goal') {
      goalControl.setValidators([Validators.required]);
    }

    if (value === 'task') {
      taskControl.setValidators([Validators.required]);
    }

    goalControl.updateValueAndValidity({emitEvent: false});
    taskControl.updateValueAndValidity({emitEvent: false});
  }

  submit(): void {
    if (!this.form.valid) {
      return;
    }

    const linkType = this.form.get('linkType').value as CostLinkType;
    const payload: CostViewModel = {
      ...this.costVM,
      name: this.form.get('name').value,
      description: this.form.get('description').value || null,
      priceNet: this.normalizePriceNet(this.form.get('priceNet').value),
      incurredAt: this.form.get('incurredAt').value,
      project: this.data.projectVM['@id'],
      goal: linkType === 'goal' ? this.form.get('goal').value : null,
      task: linkType === 'task' ? this.form.get('task').value : null,
    };

    const request$ = this.costVM?.['@id']
      ? this.costDPS.putCost(payload)
      : this.costDPS.postCost(payload);

    request$.subscribe((costVM: CostViewModel) => {
      this.data.costVM = costVM;
      this.subject.next(new ModalStateEvent(ModalState.SUCCESS, this.data));
    });
  }

  private normalizePriceNet(value: unknown): string {
    const numericValue = NumberHelper.toNumber(value);
    return numericValue > 0 ? numericValue.toFixed(2) : '0.00';
  }

  public beforeModalCloseAttempt(): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmationService.confirm({
        header: this.translate.instant('shared.modal.modal_service.add_cost.exit_process_title'),
        message: this.translate.instant('shared.modal.modal_service.add_cost.exit_process_message'),
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  }
}
